import { useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Home, Users, Settings, ClipboardList, BookOpen, CalendarClock, GraduationCap, Receipt,
  Bookmark, SlidersHorizontal, ChevronLeft, BarChart3, CalendarCheck, Target, Contact,
} from 'lucide-react';
import { Button } from '../ui/button';
import { RolePill } from '../RoleSelector/RolePill';
import type { RoleContext } from '~/types';
import {
  NAV_ITEMS,
  groupEntries,
  navLabel,
  navPath,
  roleGroupFor,
  visibleEntries,
  type NavItemDef,
  type SidebarEntry,
  type SidebarLayout,
} from './sidebarLayout';
import {
  sectionPath,
  sectionsFor,
  useContextNav,
  type SectionDef,
  type SectionIcon,
} from './contextNav';
import './Sidebar.css';

const SECTION_ICONS: Record<SectionIcon, typeof Home> = {
  home: Home,
  users: Users,
  clipboard: ClipboardList,
  chart: BarChart3,
  attendance: CalendarCheck,
  target: Target,
  calendar: CalendarClock,
  receipt: Receipt,
};

const ICONS: Record<NavItemDef['icon'], typeof Home> = {
  home: Home,
  users: Users,
  clipboard: ClipboardList,
  calendar: CalendarClock,
  attendance: CalendarCheck,
  book: BookOpen,
  graduation: GraduationCap,
  staff: Contact,
  settings: Settings,
  receipt: Receipt,
};

export interface StudentLink {
  personId: string;
  name: string;
  path: string;
}

export interface ClassLink {
  id: string;
  name: string;
  path: string;
}

interface SidebarProps {
  school: { id: string; name: string; slug: string } | null;
  /**
   * A parent's children, each with a page of their own. Unlike the built-in
   * links these come from the family's data, so they are given to the sidebar
   * rather than arranged in it.
   */
  studentLinks?: ReadonlyArray<StudentLink>;
  /**
   * The classes this person teaches this term, each with a page of their own.
   * Like a parent's children, these come from their assignments rather than
   * from an arrangement they made, so the sidebar is given them.
   */
  classLinks?: ReadonlyArray<ClassLink>;
  /** Whether the viewer instructs any class in the selected year. */
  teachesAClass?: boolean;
  roleContext: RoleContext;
  layout: SidebarLayout;
  onOpenSettings: () => void;
  onNavigate?: () => void;
}

/**
 * The user's own arrangement of links and saved views, filed under headings
 * the sidebar works out for itself. Nothing collapses: every destination this
 * role has is on screen, so finding one is reading rather than remembering.
 */
function Sidebar({ school, studentLinks, classLinks, teachesAClass, roleContext, layout, onOpenSettings, onNavigate }: SidebarProps) {
  const { schoolSlug: schoolSlugParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const schoolSlug = schoolSlugParam ?? school?.slug ?? '';
  const roleGroup = roleGroupFor(roleContext?.selectedRole);
  const yearSlug = roleContext?.selectedSchoolYear?.slug ?? null;
  const currentUrl = `${location.pathname}${location.search || ''}`;
  const rolesHere = roleContext?.accessibleSchools?.find(
    (accessible) => accessible?.school?.id === roleContext?.selectedSchool?.id
  )?.roles ?? [];
  const { contextNav } = useContextNav();
  const exclusive = !!contextNav?.exclusive;

  const groups = useMemo(
    () => {
      const visible = visibleEntries(layout, { roleGroup, hasYear: !!yearSlug, teachesAClass });
      // The context group already offers its own base and its way back out, so
      // drop any main-nav link pointing at the same place rather than showing
      // it twice. This hides "My Teaching" while you are inside teaching or a
      // class, and leaves an admin's "Courses" alone, since their way out of a
      // class goes back where they came from, or to its one course.
      const covered = contextNav
        ? new Set([contextNav.basePath, contextNav.backLink?.path].filter(Boolean))
        : null;
      const entries = covered
        ? visible.filter((entry) => {
          if (entry.type !== 'link') return true;
          const path = navPath(entry.key, schoolSlug, yearSlug);
          return !(path && covered.has(path));
        })
        : visible;
      return groupEntries(entries, roleGroup);
    },
    [layout, roleGroup, yearSlug, teachesAClass, contextNav, schoolSlug]
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const isItemActive = (item: SidebarEntry) => {
    if (item.type === 'view') return currentUrl === item.url;
    const path = navPath(item.key, schoolSlug, yearSlug);
    return !!path && location.pathname.startsWith(path);
  };

  const renderItem = (item: SidebarEntry) => {
    const active = isItemActive(item);
    if (item.type === 'view') {
      return (
        <li key={`view:${item.id}`}>
          <Button
            variant={active ? 'secondary' : 'ghost'}
            className="w-full justify-start gap-2"
            onClick={() => handleNavigate(item.url)}
            title={item.url}
          >
            <Bookmark className="h-4 w-4" />
            <span className="truncate">{item.name}</span>
          </Button>
        </li>
      );
    }
    const path = navPath(item.key, schoolSlug, yearSlug);
    if (!path) return null;
    const Icon = ICONS[NAV_ITEMS[item.key].icon];
    return (
      <li key={`link:${item.key}`}>
        <Button
          variant={active ? 'secondary' : 'ghost'}
          className="w-full justify-start gap-2"
          onClick={() => handleNavigate(path)}
        >
          <Icon className="h-4 w-4" />
          {navLabel(item.key, roleGroup)}
        </Button>
      </li>
    );
  };

  /**
   * Where you have drilled into — a class, or My Teaching. It sits above your
   * own arrangement rather than in it: this is where you are, not a link you
   * chose to keep, so it appears on the way in and disappears on the way out.
   */
  const renderSection = (basePath: string, section: SectionDef) => {
    const path = sectionPath(basePath, section);
    // The overview is the base path itself, so only an exact match counts —
    // otherwise it would light up on every section beneath it.
    const active = section.path
      ? location.pathname === path || location.pathname.startsWith(`${path}/`)
      : location.pathname === path;
    const Icon = SECTION_ICONS[section.icon];
    return (
      <li key={`section:${section.key}`}>
        <Button
          variant={active ? 'secondary' : 'ghost'}
          className="w-full justify-start gap-2 pl-6"
          onClick={() => handleNavigate(path)}
        >
          <Icon className="h-4 w-4" />
          {section.label}
        </Button>
      </li>
    );
  };

  const renderContextGroup = () => {
    if (!contextNav) return null;
    const sections = sectionsFor(contextNav.sections, roleGroup === 'admin');
    return (
      <div
        className={`sidebar-section sidebar-context-group${contextNav.exclusive ? ' sidebar-context-only' : ''}`}
        data-testid="sidebar-context-group"
      >
        {contextNav.backLink && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              const { go, path } = contextNav.backLink!;
              if (go) {
                go();
                onNavigate?.();
              } else {
                handleNavigate(path);
              }
            }}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {contextNav.backLink.label}
          </Button>
        )}
        <h2
          className={`sidebar-group-heading truncate ${contextNav.backLink ? 'pt-2' : ''}`}
          title={contextNav.title}
        >
          {contextNav.title}
        </h2>
        {contextNav.subtitle && (
          <p className="truncate px-4 pb-1 text-xs text-muted-foreground">{contextNav.subtitle}</p>
        )}
        <ul className="mt-1" aria-label={contextNav.title}>
          {sections.map((section) => renderSection(contextNav.basePath, section))}
        </ul>
        {contextNav.links && contextNav.links.length > 0 && (
          <>
            <h2 className="sidebar-group-heading pt-3">{contextNav.links[0].heading}</h2>
            <ul className="mt-1" aria-label={contextNav.links[0].heading}>
              {contextNav.links.map((link) => {
                const active = location.pathname === link.path
                  || location.pathname.startsWith(`${link.path}/`);
                return (
                  <li key={link.id}>
                    <Button
                      variant={active ? 'secondary' : 'ghost'}
                      className="w-full justify-start gap-2 pl-6"
                      onClick={() => handleNavigate(link.path)}
                    >
                      <GraduationCap className="h-4 w-4 shrink-0" />
                      <span className="truncate">{link.name}</span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderClassGroup = () => {
    if (!classLinks || classLinks.length === 0) return null;
    return (
      <div className="sidebar-section sidebar-class-group" data-testid="sidebar-class-group">
        <h2 className="sidebar-group-heading">Classes</h2>
        <ul className="mt-2" aria-label="Classes">
          {classLinks.map((classLink) => {
            const active = location.pathname === classLink.path
              || location.pathname.startsWith(`${classLink.path}/`);
            return (
              <li key={classLink.id}>
                <Button
                  variant={active ? 'secondary' : 'ghost'}
                  className="w-full justify-start gap-2 pl-6"
                  onClick={() => handleNavigate(classLink.path)}
                  title={classLink.name}
                >
                  <BookOpen className="h-4 w-4 shrink-0" />
                  <span className="truncate">{classLink.name}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderStudentGroup = () => {
    if (!studentLinks || studentLinks.length === 0) return null;
    return (
      <div className="sidebar-section sidebar-student-group" data-testid="sidebar-student-group">
        <h2 className="sidebar-group-heading">Students</h2>
        <ul className="mt-2" aria-label="Students">
          {studentLinks.map((student) => {
            const active = location.pathname === student.path
              || location.pathname.startsWith(`${student.path}/`);
            return (
              <li key={student.personId}>
                <Button
                  variant={active ? 'secondary' : 'ghost'}
                  className="w-full justify-start gap-2 pl-6"
                  onClick={() => handleNavigate(student.path)}
                >
                  <GraduationCap className="h-4 w-4" />
                  <span className="truncate">{student.name}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <nav className="sidebar-nav" aria-label="Sidebar">
      {renderContextGroup()}

      {/*
        Inside a class the context group is the entire sidebar. Showing the
        school's nav underneath it asks someone teaching a lesson to read past
        Enrollments and Families to find the roster; the back link is the way
        out, and it is the only one they need.
      */}
      {!exclusive && (
      <div className="sidebar-section">
        {groups.map((group) => (
          <div key={group.key} className="sidebar-group">
            {group.label && <h2 className="sidebar-group-heading">{group.label}</h2>}
            <ul aria-label={group.label ?? undefined}>{group.entries.map(renderItem)}</ul>
          </div>
        ))}
      </div>
      )}

      {!exclusive && renderClassGroup()}

      {!exclusive && renderStudentGroup()}

      {/*
        No role picker here any more. Someone who administers the school and
        teaches a class is one person with one job: the way into the teaching
        views is the Teaching link, not a mode to switch into first.
      */}
      <div className="sidebar-footer flex items-center justify-between gap-2">
        <div className="min-w-0">
          <RolePill roles={rolesHere} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 gap-2"
          onClick={onOpenSettings}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Settings
        </Button>
      </div>
    </nav>
  );
}

export default Sidebar;
